import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLocale } from '../i18n/LocaleContext';
import { formatPrice, useCatalog } from '../billing/catalog';
import PaymentModal from './PaymentModal';

const Pricing = ({ headingLevel = 'h1' }) => {
  const { currency } = useLocale();
  // A language change also changes the catalogue. Reset any open checkout and
  // billing period so a selection from the previous currency cannot carry over.
  return <LocalizedPricing key={currency} headingLevel={headingLevel} />;
};

const LocalizedPricing = ({ headingLevel }) => {
  const { t, locale, currency, path, appLink } = useLocale();
  const catalog = useCatalog(currency);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paymentPeriod, setPaymentPeriod] = useState('monthly');
  const prices = catalog.prices.filter(price => price.plan === 'individual');
  const monthly = prices.find(price => price.interval === 'monthly');
  const yearly = prices.find(price => price.interval === 'yearly');
  const selected = prices.find(price => price.interval === paymentPeriod) || monthly;
  const available = Boolean(selected?.available);

  const subscribe = event => {
    event.preventDefault();
    if (selected?.available) window.location.assign(appLink({ plan: 'individual', interval: selected.interval }));
  };

  return (
    <>
      <PricingSection>
        <PageTitle as={headingLevel}>{t('pricing.title')}</PageTitle>
        <PageSubtitle>{t('pricing.description')}</PageSubtitle>
        <PricingGrid>
          <PricingCard>
            <CardHeader>
              <TierName>{t('pricing.trial')}</TierName>
              <TierPrice>{t('pricing.free')}<SpanText>{t('pricing.sevenDays')}</SpanText></TierPrice>
            </CardHeader>
            <FeatureList>{['noCard', 'fullAccess', 'aiNotes', 'thanks'].map(key => <FeatureItem key={key}>{t(`pricing.${key}`)}</FeatureItem>)}</FeatureList>
            <ActionButton href={appLink()} target="_blank" rel="noopener noreferrer">{t('pricing.start')}</ActionButton>
          </PricingCard>
          <PricingCard $highlighted>
            <PopularBadge>{t('pricing.popular')}</PopularBadge>
            <CardHeader>
              <TierName>{t('pricing.individual')}</TierName>
              {monthly && <TierPrice>{formatPrice(monthly, locale)}<SpanText>{t('pricing.perMonth')}</SpanText></TierPrice>}
              {yearly && <AnnualPrice>{formatPrice(yearly, locale)}<SpanText>{t('pricing.perYear')}</SpanText></AnnualPrice>}
              {monthly?.taxBehavior === 'inclusive' && <AnnualPrice>{t('pricing.vatIncluded')}</AnnualPrice>}
              {monthly?.taxBehavior === 'exclusive' && <AnnualPrice>{t('pricing.vatAdditional')}</AnnualPrice>}
            </CardHeader>
            <FeatureList>{['unlimited', 'individualUse', 'emailSupport', 'hugs'].map(key => <FeatureItem key={key}>{t(`pricing.${key}`)}</FeatureItem>)}</FeatureList>
            <ActionButton as="button" type="button" disabled={!available} onClick={() => setIsModalOpen(true)}>
              {available ? t('pricing.subscribe') : t(currency === 'eur' ? 'pricing.comingSoon' : 'pricing.unavailable')}
            </ActionButton>
            {!available && <AnnualPrice role="status">{t('pricing.tryMeanwhile')}</AnnualPrice>}
          </PricingCard>
          <PricingCard>
            <CardHeader><TierName>{t('pricing.enterprise')}</TierName><TierPrice>{t('pricing.custom')}</TierPrice></CardHeader>
            <FeatureList>{['teams', 'volume', 'priority', 'biscuits'].map(key => <FeatureItem key={key}>{t(`pricing.${key}`)}</FeatureItem>)}</FeatureList>
            <ActionButton as={Link} to={path('/contact')}>{t('pricing.contact')}</ActionButton>
          </PricingCard>
        </PricingGrid>
      </PricingSection>
      <PaymentModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} prices={prices} selectedPrice={selected}
        onPeriodChange={setPaymentPeriod} onSubmit={subscribe} />
    </>
  );
};

// Styled Components
const PricingSection = styled.section`
  padding: 100px 20px;
  background-color: var(--light-gray);
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 15px;
  color: var(--primary);
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  text-align: center;
  margin-bottom: 60px;
  color: var(--secondary);
  max-width: 600px;
  margin-inline-start: auto;
  margin-inline-end: auto;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 40px;
  }
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 25px;
  margin-bottom: 80px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const PricingCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: ${props => props.$highlighted
    ? '0 12px 25px rgba(0, 0, 0, 0.15)'
    : '0 5px 15px rgba(0, 0, 0, 0.05)'};
  border: ${props => props.$highlighted ? '2px solid var(--primary)' : '1px solid rgba(0, 0, 0, 0.05)'};
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  &:hover {
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: 30px;
  inset-inline-end: -60px;
  background: var(--primary);
  color: white;
  padding: 8px 0;
  font-size: 0.8rem;
  font-weight: 600;
  transform: rotate(calc(45deg * var(--inline-direction)));
  z-index: 1;
  width: 200px;
  text-align: center;
`;

const CardHeader = styled.div`
  margin-bottom: 25px;
`;

const TierName = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 10px;
  color: var(--primary-dark);
`;

const TierPrice = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 5px;
`;

const AnnualPrice = styled.div`
  font-size: 1rem;
  color: var(--dark-gray);
  margin-top: 5px;
`;

const SpanText = styled.span`
  font-size: 1rem;
  font-weight: 400;
  color: var(--secondary);
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 30px;
  flex-grow: 1;
`;

const FeatureItem = styled.li`
  padding-block: 8px;
  padding-inline: 25px 0;
  position: relative;
  font-size: 0.95rem;
  color: var(--dark-gray);

  &:before {
    content: "✓";
    position: absolute;
    inset-inline-start: 0;
    color: ${props => props.$highlighted ? 'var(--primary)' : 'var(--secondary)'};
    font-weight: ${props => props.$highlighted ? '700' : '400'};
  }

  ${props => props.$highlighted && `
    font-weight: 600;
    color: var(--primary-dark);
  `}
`;

const ActionButton = styled.a`
  display: inline-block;
  padding: 12px 20px;
  background: var(--gradient);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  text-align: center;
  text-decoration: none;

  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 7px 14px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

export default Pricing;
